import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { TagResponse } from "common";
import { type JSX, useEffect, useState } from "react";

const PRESET_COLORS = [
  "#EF4444",
  "#F97316",
  "#F59E0B",
  "#EAB308",
  "#22C55E",
  "#10B981",
  "#14B8A6",
  "#06B6D4",
  "#3B82F6",
  "#6366F1",
  "#8B5CF6",
  "#A855F7",
  "#EC4899",
  "#F43F5E",
  "#6B7280",
  "#1F2937",
];

interface TagFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; color?: string }) => void;
  tag?: TagResponse | null;
  isPending?: boolean;
}

export default function TagFormModal({
  open,
  onClose,
  onSubmit,
  tag,
  isPending,
}: TagFormModalProps): JSX.Element {
  const [name, setName] = useState("");
  const [color, setColor] = useState<string>(PRESET_COLORS[4]);

  useEffect(() => {
    if (tag) {
      setName(tag.name);
      setColor(tag.color ?? PRESET_COLORS[4]);
    } else {
      setName("");
      setColor(PRESET_COLORS[4]);
    }
  }, [tag, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit({
      name: name.trim(),
      color: color || undefined,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
      <div className="fixed inset-0 z-10 overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
            <div className="absolute right-0 top-0 pr-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md bg-white text-gray-400 hover:text-gray-500">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <Dialog.Title className="text-lg font-semibold leading-6 text-gray-900 mb-4">
              {tag ? "Edit Tag" : "Create Tag"}
            </Dialog.Title>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="tag-name"
                  className="block text-sm font-medium text-gray-700">
                  Name
                </label>
                <input
                  id="tag-name"
                  type="text"
                  required
                  maxLength={255}
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="e.g. Production"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Color
                </label>
                <div className="flex flex-wrap gap-2">
                  {PRESET_COLORS.map(c => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setColor(c)}
                      className={`h-8 w-8 rounded-full border-2 transition-all ${
                        color === c
                          ? "border-gray-900 scale-110"
                          : "border-transparent hover:border-gray-300"
                      }`}
                      style={{ backgroundColor: c }}
                      title={c}
                    />
                  ))}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <label
                    htmlFor="custom-color"
                    className="text-xs text-gray-500">
                    Custom:
                  </label>
                  <input
                    id="custom-color"
                    type="color"
                    value={color}
                    onChange={e => setColor(e.target.value)}
                    className="h-8 w-8 cursor-pointer rounded border-0 p-0"
                  />
                  <span className="text-xs text-gray-500 font-mono">
                    {color}
                  </span>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                <button
                  type="submit"
                  disabled={!name.trim() || isPending}
                  className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 sm:col-start-2">
                  {isPending ? "Saving..." : tag ? "Update" : "Create"}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0">
                  Cancel
                </button>
              </div>
            </form>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
}
